import React, { useEffect, useState, useId, useRef, useCallback } from 'react'
import JSZip from 'jszip'
import { Checkbox, Loader, Tooltip, Whisper, IconButton, Drawer } from 'rsuite'
import { FaInfoCircle } from 'react-icons/fa'
import cn from 'classnames'
import { pubhubIndexedDb } from '@/utils/indexedDBHelper'
import pubhubData from '@/utils/apiHelpers/pubhubData'
import useTranslation from '@/hooks/useTranslation'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useAnalytics, ANALYTICS_EVENTS } from '@/hooks/useAnalytics'
import { useSelector } from 'react-redux'
import { configSelectors } from '@/store/configs'
import { generalHelpers } from '@tweddle-se-team/common/helpers'
import { generalConstants } from '@tweddle-se-team/common/constants'
import styles from './saveToLocalCheckbox.module.scss'

const { renamePubType } = generalHelpers
const { WTY } = generalConstants

// Status constants for the offline operation
const OFFLINE_STATUS = {
  IDLE: 'idle',
  DOWNLOADING: 'downloading',
  REMOVING: 'removing',
  DOWNLOAD_SUCCESS: 'download_success',
  REMOVE_SUCCESS: 'remove_success',
  ERROR: 'error'
}

const savePublicationToIndexedDb = async publication => {
  const {
    contents: { ditaId }
  } = publication

  // Fetch vehicle image URL for offline use
  let vehicleImageUrl = null
  try {
    const productImages = await pubhubData.getProductsImages()
    const { GetImageByConfig } = await import('@/utils/productHelper')
    vehicleImageUrl = GetImageByConfig(productImages, publication.model, publication.modelType, publication.year)
  } catch (error) {
    console.warn('[savePublicationToIndexedDb] Failed to fetch vehicle image:', error)
  }

  const publicationInfo = await pubhubData.getPublication(ditaId)
  const url = publicationInfo.bundle.url
  const response = await fetch(url)
  const blob = await response.blob()
  const zip = new JSZip()
  const zipArchive = await zip.loadAsync(blob)

  const files = await Promise.all(
    Object.keys(zipArchive.files).map(async filename => ({
      filename,
      data: await zipArchive.files[filename].async('arraybuffer')
    }))
  )
  const contents = await zipArchive.files['toc.json'].async('string')

  return pubhubIndexedDb.publications.add({
    ditaId,
    contents: JSON.parse(contents),
    publicationFromApi: publication,
    vehicleImageUrl,
    files
  })
}

const removePublicationFromIndexDb = ditaId => pubhubIndexedDb.publications.where('ditaId').equals(ditaId).delete()

const TooltipContent = () => {
  const t = useTranslation()
  return (
    <div>
      <p>
        {t(
          'offline_available_tooltip',
          'Select the checkbox to make this document available offline. Save a bookmark to your home screen on mobile for easy access offline.'
        )}
      </p>
      <br />
      <p>{t('iphone_users', 'iPhone users: please use Safari for the best experience.')}</p>
    </div>
  )
}

const SaveToLocalCheckbox = ({ publication, onRemove }) => {
  const [loading, setLoading] = useState(false)
  const [isCached, setIsCached] = useState(null)
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [offlineStatus, setOfflineStatus] = useState(OFFLINE_STATUS.IDLE)
  const isMobile = useIsMobile()
  const t = useTranslation()
  const analytics = useAnalytics()
  const publicationTypes = useSelector(configSelectors.selectConfigByKey('publicationTypes'))
  const descriptionId = useId()
  const liveRegionId = useId()
  const downloadStatusRegionId = useId()
  const checkboxRef = useRef(null)

  const {
    contents: { ditaId }
  } = publication

  const getLabel = isCached =>
    isCached ? (
      <span>{t('finding_vehicle_publications_offline_available')}</span>
    ) : (
      <span>{t('finding_vehicle_publications_make_offline')}</span>
    )

  // Status messages for screen readers
  const getStatusMessage = useCallback(() => {
    switch (offlineStatus) {
      case OFFLINE_STATUS.DOWNLOADING:
        return t('offline_downloading_status', 'Downloading publication for offline use, please wait...')
      case OFFLINE_STATUS.REMOVING:
        return t('offline_removing_status', 'Removing publication from offline storage, please wait...')
      case OFFLINE_STATUS.DOWNLOAD_SUCCESS:
        return t('offline_download_complete', 'Download complete. Publication is now available offline.')
      case OFFLINE_STATUS.REMOVE_SUCCESS:
        return t('offline_remove_complete', 'Publication removed from offline storage.')
      case OFFLINE_STATUS.ERROR:
        return t('offline_operation_error', 'Operation failed. Please try again.')
      default:
        return ''
    }
  }, [offlineStatus, t])

  const disabled = !ditaId

  const addToCache = async () => {
    setLoading(true)
    setOfflineStatus(OFFLINE_STATUS.DOWNLOADING)
    try {
      await savePublicationToIndexedDb(publication)
      setIsCached(true)
      setOfflineStatus(OFFLINE_STATUS.DOWNLOAD_SUCCESS)

      // Send individual parameters for better GA4 analysis
      const { year, model, modelType, publicationType } = publication
      const publicationTypeName = renamePubType(publicationTypes, publicationType, 'label')

      analytics.track(ANALYTICS_EVENTS.OFFLINE_SAVE, {
        dita_id: ditaId,
        vehicle_year: year,
        vehicle_model: model,
        vehicle_model_type: modelType,
        publication_type: publicationTypeName,
        part_number: publication?.partNumber
      })
    } catch (error) {
      console.error('[SaveToLocalCheckbox] Failed to save publication:', error)
      setOfflineStatus(OFFLINE_STATUS.ERROR)
    } finally {
      setLoading(false)
      // Restore focus to checkbox after operation completes
      setTimeout(() => {
        checkboxRef.current?.focus()
        // Clear status message after announcement
        setTimeout(() => setOfflineStatus(OFFLINE_STATUS.IDLE), 2000)
      }, 100)
    }
  }

  const removeFromCache = async () => {
    setLoading(true)
    setOfflineStatus(OFFLINE_STATUS.REMOVING)
    try {
      await removePublicationFromIndexDb(ditaId)
      setIsCached(false)
      setOfflineStatus(OFFLINE_STATUS.REMOVE_SUCCESS)
      analytics.track(ANALYTICS_EVENTS.OFFLINE_REMOVE, {
        dita_id: ditaId
      })
      onRemove && onRemove(ditaId)
    } catch (error) {
      console.error('[SaveToLocalCheckbox] Failed to remove publication:', error)
      setOfflineStatus(OFFLINE_STATUS.ERROR)
    } finally {
      setLoading(false)
      // Restore focus to checkbox after operation completes
      setTimeout(() => {
        checkboxRef.current?.focus()
        // Clear status message after announcement
        setTimeout(() => setOfflineStatus(OFFLINE_STATUS.IDLE), 2000)
      }, 100)
    }
  }

  // Handle checkbox interaction - prevent action when loading using aria-disabled pattern
  const handleChange = async (value, checked) => {
    if (loading || disabled) return
    if (checked) await addToCache()
    else await removeFromCache()
  }

  useEffect(() => {
    if (!ditaId) {
      setIsCached(false)
      return
    }

    pubhubIndexedDb.publications
      .where('ditaId')
      .equals(ditaId)
      .count()
      .then(count => setIsCached(!!count))
  }, [ditaId])

  if (!publication?.contents?.ditaId || publication.publicationType === WTY) return null

  return (
    <div className="flex-sb mt-10">
      <div className={cn(styles.container, { [styles.checkboxLoading]: loading })}>
        <Checkbox
          inputRef={checkboxRef}
          disabled={disabled}
          aria-disabled={loading}
          onChange={handleChange}
          checked={isCached}
          aria-describedby={descriptionId}
          aria-busy={loading}
        >
          {getLabel(isCached)}
        </Checkbox>
        {/* Desktop: Tooltip on click/focus */}
        {!isMobile && (
          <Whisper
            placement="top"
            trigger={['click', 'focus']}
            speaker={
              <Tooltip>
                <TooltipContent />
              </Tooltip>
            }
            onOpen={() => setIsInfoOpen(true)}
            onClose={() => setIsInfoOpen(false)}
          >
            <IconButton
              icon={<FaInfoCircle />}
              appearance="link"
              size="md"
              className={styles.infoButton}
              aria-label={t('offline_info_button', 'More information about offline availability')}
              aria-expanded={isInfoOpen}
              aria-controls={liveRegionId}
            />
          </Whisper>
        )}

        {/* Mobile: Drawer sliding from bottom */}
        {isMobile && (
          <>
            <IconButton
              icon={<FaInfoCircle />}
              appearance="link"
              size="md"
              className={styles.infoButton}
              aria-label={t('offline_info_button', 'More information about offline availability')}
              aria-expanded={isInfoOpen}
              aria-controls={liveRegionId}
              onClick={() => setIsInfoOpen(true)}
            />
            <Drawer open={isInfoOpen} onClose={() => setIsInfoOpen(false)} placement="bottom" size="xs">
              <Drawer.Header className={styles.drawerHeader} />
              <Drawer.Body className={styles.drawerBody}>
                <div>
                  <FaInfoCircle />
                </div>
                <TooltipContent />
              </Drawer.Body>
            </Drawer>
          </>
        )}

        {/* Visually hidden description for screen readers (always available) */}
        <span id={descriptionId} className={styles.srOnly}>
          <TooltipContent />
        </span>
        {/* Live region that announces tooltip/drawer content when opened */}
        <span id={liveRegionId} role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
          {isInfoOpen ? <TooltipContent /> : null}
        </span>
        {/* Live region for download status - uses assertive to ensure immediate announcement */}
        <span
          id={downloadStatusRegionId}
          role="status"
          aria-live="assertive"
          aria-atomic="true"
          className={styles.srOnly}
        >
          {getStatusMessage()}
        </span>
      </div>
      {loading && (
        <span className={styles.loaderWrapper} aria-hidden="true">
          <Loader size="xs" content={t('offline_downloading_visual', 'Downloading...')} />
        </span>
      )}
    </div>
  )
}

export default SaveToLocalCheckbox
